import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Plus, Trash2, GraduationCap, ChevronDown, ChevronUp } from "lucide-react";
import {
  draftTeachingContentSchema,
  type DraftTeachingContent,
  type DraftTeachingItem,
  type TeachingLevel,
  type TeachingRole,
} from "@profolio/validators";

interface TeachingEditorProps {
  content: Record<string, unknown>;
  onChange: (content: DraftTeachingContent) => void;
}

export function TeachingEditor({ content, onChange }: TeachingEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);

  const parseResult = draftTeachingContentSchema.safeParse(content);
  const data: DraftTeachingContent = parseResult.success
    ? parseResult.data
    : { heading: "Teaching & Courses", displayStyle: "grouped_by_level", items: [] };

  const heading = data.heading || "Teaching & Courses";
  const displayStyle = data.displayStyle || "grouped_by_level";
  const items: DraftTeachingItem[] = data.items || [];

  const notifyChange = (
    newHeading: string,
    newStyle: "grouped_by_level" | "grouped_by_year" | "list",
    newItems: DraftTeachingItem[]
  ) => {
    onChange({
      heading: newHeading,
      displayStyle: newStyle,
      items: newItems,
    });
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    notifyChange(e.target.value, displayStyle, items);
  };

  const handleStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as "grouped_by_level" | "grouped_by_year" | "list";
    notifyChange(heading, val, items);
  };

  const addItem = () => {
    if (items.length >= 50) return;
    const newId = crypto.randomUUID();
    const newItem: DraftTeachingItem = {
      id: newId,
      courseCode: "",
      courseTitle: "",
      level: "undergraduate",
      role: "instructor",
      institution: "",
      term: "",
      description: "",
      courseUrl: "",
      syllabusUrl: "",
    };
    const updated = [newItem, ...items];
    setExpandedIndex(0);
    setNewlyAddedId(newId);
    notifyChange(heading, displayStyle, updated);
  };

  const updateItem = (index: number, patch: Partial<DraftTeachingItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...patch };
    notifyChange(heading, displayStyle, updated);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    if (expandedIndex === index) setExpandedIndex(null);
    notifyChange(heading, displayStyle, updated);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="teaching-heading" className="text-xs font-semibold">
            Section Heading
          </Label>
          <Input
            id="teaching-heading"
            value={heading}
            onChange={handleHeadingChange}
            placeholder="Teaching & Courses"
            className="mt-1 text-xs"
          />
        </div>
        <div>
          <Label htmlFor="teaching-style" className="text-xs font-semibold">
            Organize courses by
          </Label>
          <select
            id="teaching-style"
            value={displayStyle}
            onChange={handleStyleChange}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm transition-colors"
          >
            <option value="grouped_by_level" className="bg-card text-foreground">Academic Level</option>
            <option value="grouped_by_year" className="bg-card text-foreground">Term / Year</option>
            <option value="list" className="bg-card text-foreground">One list</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Courses & Curriculum ({items.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Add the undergraduate, graduate, or seminar courses you teach.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={addItem}
            disabled={items.length >= 50}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Course
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title="No courses added yet"
            description="Showcase your lectures, undergraduate courses, syllabi, and student resources."
            actionLabel="Add First Course"
            onAction={addItem}
          />
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <div
                  key={item.id || index}
                  className="rounded-lg border bg-card p-3.5 transition-all shadow-2xs hover:border-primary/30"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className="flex-1 cursor-pointer font-medium text-xs flex items-center gap-2 min-w-0"
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    >
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted shrink-0">
                        {item.courseCode || "CODE"}
                      </span>
                      <span className="truncate text-foreground font-semibold">
                        {item.courseTitle || "Untitled Course"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className="h-7 w-7 text-muted-foreground"
                      >
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3.5 space-y-3 pt-3.5 border-t">
                      {/* 1. Essential Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Course Code *</Label>
                          <Input
                            placeholder="e.g. CS 301"
                            value={item.courseCode}
                            autoFocus={newlyAddedId === item.id}
                            onFocus={() => setNewlyAddedId(null)}
                            onChange={(e) => updateItem(index, { courseCode: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs font-medium">Course Title *</Label>
                          <Input
                            placeholder="e.g. Distributed Systems & Cloud Computing"
                            value={item.courseTitle}
                            onChange={(e) => updateItem(index, { courseTitle: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Academic Level</Label>
                          <select
                            value={item.level}
                            onChange={(e) => updateItem(index, { level: e.target.value as TeachingLevel })}
                            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm"
                          >
                            <option value="undergraduate" className="bg-card text-foreground">Undergraduate</option>
                            <option value="postgraduate" className="bg-card text-foreground">Postgraduate / Masters</option>
                            <option value="phd" className="bg-card text-foreground">Ph.D. Seminar</option>
                            <option value="executive" className="bg-card text-foreground">Executive Education</option>
                            <option value="other" className="bg-card text-foreground">Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Teaching Role</Label>
                          <select
                            value={item.role}
                            onChange={(e) => updateItem(index, { role: e.target.value as TeachingRole })}
                            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm"
                          >
                            <option value="instructor" className="bg-card text-foreground">Lead Instructor</option>
                            <option value="co_instructor" className="bg-card text-foreground">Co-Instructor</option>
                            <option value="teaching_assistant" className="bg-card text-foreground">Teaching Assistant</option>
                            <option value="guest_lecturer" className="bg-card text-foreground">Guest Lecturer</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Term / Semester</Label>
                          <Input
                            placeholder="e.g. Fall 2025"
                            value={item.term || ""}
                            onChange={(e) => updateItem(index, { term: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      {/* 2. Progressive Disclosure: Syllabi, URLs & Description */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Department / Institution</Label>
                          <Input
                            placeholder="e.g. Faculty of Science, Univ. of Colombo"
                            value={item.institution || ""}
                            onChange={(e) => updateItem(index, { institution: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Course Website / LMS Link</Label>
                          <Input
                            placeholder="https://courses.example.edu/cs301"
                            value={item.courseUrl || ""}
                            onChange={(e) => updateItem(index, { courseUrl: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Syllabus PDF URL</Label>
                        <Input
                          placeholder="https://example.com/syllabus.pdf"
                          value={item.syllabusUrl || ""}
                          onChange={(e) => updateItem(index, { syllabusUrl: e.target.value })}
                          className="mt-1 text-xs"
                        />
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Course Description & Overview</Label>
                        <Textarea
                          placeholder="Overview of topics, prerequisites, and learning objectives..."
                          value={item.description || ""}
                          onChange={(e) => updateItem(index, { description: e.target.value })}
                          rows={3}
                          className="mt-1 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
